import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, fieldErrors } from "../../../../api/client";
import { twilioApi, type TwilioView } from "../../../../api/twilio";
import { Button, Field, TextInput } from "../../../../components/ui";
import { Card, CodeField, SourceTag } from "./parts";

type Key = "account_sid" | "auth_token" | "api_key_sid" | "api_key_secret";
const LABELS: [Key, string, string, boolean][] = [
  ["account_sid", "Account SID", "Starts with AC. On your Twilio Console home page, under Account info.", false],
  ["auth_token", "Auth Token", "Twilio signs incoming texts with it. Console home page → Account info → Show.", true],
  ["api_key_sid", "API Key SID (optional)", "Starts with SK. If set with its secret, outgoing texts use the API key instead of the Auth Token.", false],
  ["api_key_secret", "API Key Secret (optional)", "Shown by Twilio only once, when the key is created.", true],
];

function Current({ v, k }: { v: TwilioView; k: Key }) {
  const f = v.fields[k];
  return (
    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-400" data-testid={`twilio-current-${k}`}>
      {f.set ? <>set · ends in <span className="font-mono text-zinc-200">{f.ends_in}</span></> : "not set"}
      <SourceTag source={f.source} testId={`twilio-source-${k}`} />
    </p>
  );
}

export function CredentialsCard({ v, onChange }: { v: TwilioView; onChange: (n: TwilioView) => void }) {
  const [form, setForm] = useState<Record<Key | "phone_number", string>>({
    account_sid: "", auth_token: "", api_key_sid: "", api_key_secret: "",
    phone_number: (v.page_saved.phone_number as string | null) ?? v.suggested_phone_number,
  });
  const [removeKey, setRemoveKey] = useState(false);
  const [code, setCode] = useState("");
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<"" | "save" | "remove">("");
  const set = (k: string, val: string) => setForm((f) => ({ ...f, [k]: val }));
  const saved = v.page_saved.auth_token as { set: boolean } | null;
  const run = async (which: "save" | "remove") => {
    setBusy(which); setErrs({});
    try {
      const n = which === "save"
        ? await twilioApi.save({ ...form, auth_token: form.auth_token || undefined, api_key_sid: form.api_key_sid || undefined, api_key_secret: form.api_key_secret || undefined, remove_api_key: removeKey, code })
        : await twilioApi.remove(code);
      onChange(n); setCode(""); setForm((f) => ({ ...f, auth_token: "", api_key_secret: "" }));
      toast.success(which === "save" ? "Twilio credentials saved. Press Test connection next." : "Saved credentials removed");
    } catch (e) { setErrs(fieldErrors(e)); toast.error(errorMessage(e)); } finally { setBusy(""); }
  };
  return (
    <Card title="Credentials" icon={<KeyRound className="h-5 w-5 text-signal" />} testId="twilio-credentials-card">
      {v.credential_source === "env" && (
        <p className="mb-4 rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 text-sm text-amber-100" data-testid="twilio-env-override-note">
          Twilio credentials are set in Secrets (TWILIO_ACCOUNT_SID…). Those win over anything saved here.
        </p>
      )}
      <p className="mb-4 text-sm text-zinc-400">Secrets are write-only: they're encrypted when saved and never shown again. Leave a secret blank to keep the saved one.</p>
      <div className="grid gap-4 md:grid-cols-2">
        {LABELS.map(([k, label, help, secret]) => (
          <div key={k}>
            <Field label={label} htmlFor={`twilio-${k}`}>
              <TextInput id={`twilio-${k}`} type={secret ? "password" : "text"} autoComplete="off" spellCheck={false} value={form[k]}
                onChange={(e) => set(k, e.target.value.trim())} aria-invalid={!!errs[k]} placeholder={v.fields[k].set ? "Leave blank to keep" : ""} data-testid={`twilio-input-${k}`} />
            </Field>
            {errs[k] && <p className="mt-1 text-xs text-red-300" data-testid={`twilio-error-${k}`}>{errs[k]}</p>}
            <p className="mt-1 text-xs text-zinc-500">{help}</p>
            <Current v={v} k={k} />
          </div>
        ))}
        <div>
          <Field label="Twilio phone number" htmlFor="twilio-phone_number">
            <TextInput id="twilio-phone_number" value={form.phone_number} onChange={(e) => set("phone_number", e.target.value.trim())} aria-invalid={!!errs.phone_number} data-testid="twilio-input-phone_number" />
          </Field>
          {errs.phone_number && <p className="mt-1 text-xs text-red-300" data-testid="twilio-error-phone_number">{errs.phone_number}</p>}
          <p className="mt-1 flex items-center gap-2 text-xs text-zinc-400">Active: <span className="font-mono text-zinc-200" data-testid="twilio-active-phone">{v.fields.phone_number.ends_in ?? "none"}</span><SourceTag source={v.fields.phone_number.source} testId="twilio-source-phone_number" /></p>
        </div>
        {v.fields.api_key_sid.set && (
          <label className="flex items-center gap-2 self-end text-sm text-zinc-300">
            <input type="checkbox" checked={removeKey} onChange={(e) => setRemoveKey(e.target.checked)} data-testid="twilio-remove-api-key" />
            Stop using the API key (use the Auth Token for everything)
          </label>
        )}
      </div>
      <div className="mt-6 flex flex-wrap items-end gap-3">
        <div className="w-72"><CodeField value={code} onChange={setCode} id="twilio-credentials-code" /></div>
        <Button onClick={() => run("save")} loading={busy === "save"} disabled={code.length < 6 || !form.account_sid} data-testid="twilio-save-credentials">Save credentials</Button>
        {saved?.set && <Button variant="ghost" onClick={() => run("remove")} loading={busy === "remove"} disabled={code.length < 6} data-testid="twilio-remove-credentials">Remove saved credentials</Button>}
      </div>
    </Card>
  );
}
